/**
 * Phase 86 Plan 86-03 — the "Compost" design's row, rebuilt on the
 * handoff's 8-column grid (`.planning/design/mytasks-compost/README.md`,
 * "The table grid" + "Cell specs"). Supersedes the Phase 45 45-06 row.
 *
 * gridTemplateColumns LOCKED to the handoff's literal template:
 *   '3px 74px minmax(0,1fr) 96px 132px 84px 92px 74px'
 *
 * Columns: spine · CODE · TASK · CLIENT · ROT · LAST TOUCH · LOGGED · DUE ·
 * action. `MyTasksColumnLegend` imports `MY_TASK_ROW_GRID` from this file so
 * the header can never drift from the rows (locked in
 * `MyTaskRow.test.tsx:48`).
 *
 * Rot signals (zone, spine colour, meter, inline emoji) are gated on the
 * `audience` prop, mirroring `TaskCard.tsx`'s internal-only emoji gate
 * (86-CONTEXT "Internal-only gate"). My Tasks is mounted on an internal
 * route only today, so `audience="external"` is not currently reachable —
 * implemented and tested anyway per the locked decision.
 *
 * Row click / Enter now navigates directly to the full-page ticket route
 * (`/tasks/:taskRef`, MKS-144) instead of the board deep link the page used
 * before this plan (86-CONTEXT "Row click target"). `onRowClick` is kept
 * only so the existing `MyTasksProjectGroup` → `MyTasksList` → `MyTasksPage`
 * prop chain (untouched by this plan) still type-checks; the row no longer
 * relies on it to decide where to go.
 *
 * Phase 86 Plan 86-05 — TOUCH (stale zone) and REVIVE (graveyard zone) are
 * now real mutations, wired the same way +LOG already was: one popover/menu
 * per action, all sharing the identical row-click guard so opening any of
 * them blocks the row's own navigate-on-click for the duration (the row's
 * `onClick` fires on every bubbled click from inside these panels; the guard
 * — not `stopPropagation` on every internal click — is what keeps that from
 * navigating away, matching the pre-existing `+LOG` pattern).
 *
 * Phase 86 Plan 86-06 — the keyboard + responsive layer:
 *   - `useMyTasksViewportMode` (exported from here, the same file that
 *     already owns the one locked grid template) tracks `window.innerWidth`
 *     against the handoff's two breakpoints and is read by `MyTasksPage`,
 *     `MyTasksZonedList` and `MyTasksColumnLegend` so the header, the rows
 *     and the page layout can never disagree about which mode is active —
 *     one shared source, same invariant 86-03 established for the grid
 *     template string itself.
 *   - `viewportMode="narrow"` drops the CLIENT and LOGGED cells and switches
 *     to `MY_TASK_ROW_GRID_NARROW` (still the single grid the legend reads).
 *   - `viewportMode="card"` renders an entirely different, non-grid card
 *     layout (title line / meta line / footer) per the handoff's own
 *     "the table becomes cards" instruction — it does not try to force the
 *     8-column grid to survive at phone width.
 *   - `isCursor` marks the keyboard cursor row: an accent background tint +
 *     a 2px inset accent ring PLUS `aria-current="true"` in the markup
 *     itself (not colour alone — the roadmap's own success criterion), and
 *     scrolls the row into view on mount/update when it becomes the cursor.
 *   - The row now registers a `MyTaskRowHandle` (`triggerTouch`) with its
 *     parent via a plain `registerHandle` callback prop (not `forwardRef` —
 *     `MyTaskRow.test.tsx` types its render helper with
 *     `Parameters<typeof MyTaskRow>`, which requires a callable function
 *     type) so `useMyTasksKeyboard`'s `T` shortcut can open TOUCH on the
 *     cursor row through the exact same `setTouchOpen` state the TOUCH
 *     chip's own click handler already uses — not a parallel implementation.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { MyTaskDto } from '../../services/api/tasks';
import { LogTimePopover } from './LogTimePopover';
import { MtTouchPopover } from './MtTouchPopover';
import { MtReviveMenu } from './MtReviveMenu';
import { MtRotMeter } from './MtRotMeter';
import { MtActionChip } from './MtActionChip';
import { taskRotZone, taskStalenessEmoji, type TaskRotZone } from '../../utils/format';
import { ENGLISH_LABEL_FORMAT, formatDayMonthShort } from '../../utils/dateFormat';
import {
  ACCENT_TINT_HEX,
  DANGER_HEX,
  DANGER_STRONG_HEX,
  MICRO_LABEL_GREY_HEX,
  OVER_ESTIMATE_CHIP_TINT_HEX,
  STALE_DARK_HEX,
  ZONE_CHIP_STYLE,
  ZONE_SPINE_COLOR,
} from './compostTokens';

export type Density = 'comfortable' | 'compact';
export type MyTaskRowAudience = 'internal' | 'external';

/**
 * README "Responsive": below ~1100px drop CLIENT + LOGGED ("narrow"); below
 * ~700px the table becomes cards ("card"). "wide" is the unmodified 8-column
 * default this component has always rendered.
 */
export type MyTasksViewportMode = 'wide' | 'narrow' | 'card';

const NARROW_MAX_WIDTH = 1100;
const CARD_MAX_WIDTH = 700;

function resolveViewportMode(width: number): MyTasksViewportMode {
  if (width < CARD_MAX_WIDTH) return 'card';
  if (width < NARROW_MAX_WIDTH) return 'narrow';
  return 'wide';
}

/**
 * Tracks `window.innerWidth` against the two locked breakpoints. Shared by
 * `MyTasksPage` (rail stacking), `MyTasksZonedList` (which mode to hand the
 * legend + every row) and, transitively, `MyTasksColumnLegend` — one hook,
 * so the page layout, the header and the rows can never independently
 * decide they are at a different width from one another.
 */
export function useMyTasksViewportMode(): MyTasksViewportMode {
  const [mode, setMode] = useState<MyTasksViewportMode>(() =>
    typeof window === 'undefined' ? 'wide' : resolveViewportMode(window.innerWidth),
  );

  useEffect(() => {
    function handleResize() {
      setMode(resolveViewportMode(window.innerWidth));
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return mode;
}

/** Imperative handle registered via `registerHandle` — lets
 * `useMyTasksKeyboard`'s `T` shortcut open TOUCH on the cursor row through
 * the SAME `setTouchOpen` state the chip's own click handler uses. No-op on
 * a row whose zone has no touch action (the chip is +LOG or REVIVE, not
 * TOUCH). Delivered via a plain callback prop rather than `forwardRef` so
 * `MyTaskRow` stays a normal function component — `MyTaskRow.test.tsx`'s
 * `Parameters<typeof MyTaskRow>` helper requires a callable function type,
 * which a `ForwardRefExoticComponent` is not. */
export interface MyTaskRowHandle {
  triggerTouch: () => void;
}

export interface MyTaskRowProps {
  task: MyTaskDto;
  density: Density;
  isSla?: boolean;
  onRowClick?: (taskId: number) => void;
  /** Explicit override for tests — when omitted the grid is derived from
   * `viewportMode` (`wide` → the locked 8-column template, `narrow` → the
   * 6-column template with CLIENT/LOGGED dropped). Has no effect in `card`
   * mode, which does not use a grid at all. */
  gridTemplateColumns?: string;
  /** Petra UAT 2026-05-19 — index within parent project group, drives zebra stripes. */
  rowIndex?: number;
  /**
   * Gates every rot signal (emoji, spine colour, meter). Defaults to
   * 'internal' — My Tasks is an internal-only route today.
   */
  audience?: MyTaskRowAudience;
  /** Phase 86-06 — which of the three responsive layouts to render. */
  viewportMode?: MyTasksViewportMode;
  /** Phase 86-06 — true when this row is under the keyboard cursor. */
  isCursor?: boolean;
  /** Phase 86-06 — registers/unregisters this row's `MyTaskRowHandle` under
   * its own `task.taskId` with the parent list so the keyboard layer can
   * reach it without lifting the popover state itself. */
  registerHandle?: (taskId: number, handle: MyTaskRowHandle | null) => void;
}

const ROW_GRID = '3px 74px minmax(0,1fr) 96px 132px 84px 92px 74px';
/** Phase 86-06 — README "Responsive": drops CLIENT (96px) and LOGGED
 * (84px), keeping spine · CODE · TASK · ROT · DUE · action. */
const ROW_GRID_NARROW = '3px 74px minmax(0,1fr) 132px 92px 74px';

/** 2px inset accent ring for the keyboard cursor row (README "Keyboard" —
 * "keep focus visible with a 2px inset ring", in `#FC8332`). Raw hex, not
 * the `--ms-orange` CSS custom property — a `var()` reference inside a
 * `box-shadow` shorthand is parsed inconsistently by jsdom's style engine
 * in tests, and this hex is the exact same accent every zone chip already
 * uses directly (see `ZONE_CHIP_STYLE.fresh.borderHex` in compostTokens.ts). */
const CURSOR_RING_BOX_SHADOW = 'inset 0 0 0 2px #FC8332';

/** TRIAGE / IN PROGRESS / TODO / BLOCKED / DONE meta-line word colour. Kept
 * local (not compostTokens.ts) — this is the existing status-colour family
 * MtStatusPill.STATUS_MAP already inlines as raw hex, not a zone colour. */
const STATUS_WORD_COLOR: Record<string, string> = {
  TRIAGE: DANGER_HEX,
  'IN PROGRESS': '#9A3F0B',
  TODO: 'var(--ms-text-muted)',
  BLOCKED: '#6B21A8',
  DONE: '#0F7A55',
};

function metaStatusLabel(taskStateName: string | null, status: string | null): string {
  const raw = taskStateName ?? status;
  if (!raw) return 'TODO';
  return raw.toLowerCase() === 'open' ? 'TODO' : raw.toUpperCase();
}

function parseCalendarDate(value: string): Date | null {
  const datePart = value.split('T')[0] ?? '';
  const parts = datePart.split('-').map((n) => parseInt(n, 10));
  const [year, month, day] = parts;
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function startOfToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function formatDueShort(date: Date): string {
  // MKS-351 — "20 NOV" from the shared util rather than an en-GB locale call.
  // This cell already read English, so it passes the English label spec and
  // reads the same in both release-flag branches.
  return formatDayMonthShort(date, ENGLISH_LABEL_FORMAT).toUpperCase();
}

interface DueCellInfo {
  primary: string;
  secondary: string | null;
  primaryColor: string;
  primaryClass: string;
  secondaryColor: string;
}

function dueCellInfo(dueAt: string | null, isTerminal: boolean): DueCellInfo {
  if (isTerminal) {
    return {
      primary: 'closed',
      secondary: null,
      primaryColor: 'var(--ms-text-muted)',
      primaryClass: 'font-normal',
      secondaryColor: 'var(--ms-text-muted)',
    };
  }
  const due = dueAt ? parseCalendarDate(dueAt) : null;
  if (!due) {
    return {
      primary: 'no due',
      secondary: null,
      primaryColor: 'var(--ms-text-muted)',
      primaryClass: 'font-normal',
      secondaryColor: 'var(--ms-text-muted)',
    };
  }
  const diffDays = Math.round((due.getTime() - startOfToday().getTime()) / 86_400_000);
  const label = formatDueShort(due);
  if (diffDays < 0) {
    const lateDays = Math.abs(diffDays);
    return {
      primary: label,
      secondary: `${lateDays} day${lateDays === 1 ? '' : 's'} late`,
      primaryColor: DANGER_HEX,
      primaryClass: 'font-bold',
      secondaryColor: DANGER_STRONG_HEX,
    };
  }
  if (diffDays === 0) {
    return {
      primary: label,
      secondary: 'today',
      primaryColor: STALE_DARK_HEX,
      primaryClass: 'font-bold',
      secondaryColor: STALE_DARK_HEX,
    };
  }
  return {
    primary: label,
    secondary: null,
    primaryColor: '',
    primaryClass: 'font-medium text-ms-text-secondary',
    secondaryColor: '',
  };
}

export function MyTaskRow({
  task,
  density,
  onRowClick,
  gridTemplateColumns,
  rowIndex = 0,
  audience = 'internal',
  viewportMode = 'wide',
  isCursor = false,
  registerHandle,
}: MyTaskRowProps) {
  const navigate = useNavigate();
  const rowRef = useRef<HTMLDivElement>(null);
  // Petra UAT 2026-05-19 — subtle zebra stripes (~5% surface-2 tint on odd
  // rows) help the eye track wide rows. Hover state still wins via Tailwind
  // class specificity (bg-ms-surface-2/40 > inline-tint). The cursor row's
  // own accent tint always wins over the zebra tint (86-06).
  const zebraBg = rowIndex % 2 === 1 ? 'bg-ms-surface-2/30' : '';
  const [logOpen, setLogOpen] = useState(false);
  const [touchOpen, setTouchOpen] = useState(false);
  const [reviveOpen, setReviveOpen] = useState(false);
  const padding = density === 'compact' ? 'py-2' : 'py-[11px]';

  const rotVisible = audience === 'internal';
  const zone: TaskRotZone = rotVisible
    ? taskRotZone(task.lastActivityAt, { isTerminal: task.isTerminal })
    : task.isTerminal
      ? 'terminal'
      : 'fresh';
  const rotEmoji = rotVisible
    ? taskStalenessEmoji(task.lastActivityAt, { isTerminal: task.isTerminal })
    : null;
  const dimmed = zone === 'graveyard' || zone === 'terminal';

  // 86-CONTEXT "Row click target" — code turns accent on the row the SLA
  // reaction % is breaching, or on an explicitly urgent-priority task.
  const isUrgentRow =
    task.priority === 'urgent' || (task.slaReactionPct != null && task.slaReactionPct >= 100);

  const over =
    task.estimatedHours != null && task.estimatedHours > 0 && task.loggedHours > task.estimatedHours;

  const due = dueCellInfo(task.dueAt, task.isTerminal);
  const statusLabel = metaStatusLabel(task.taskStateName, task.status);
  const statusColor = STATUS_WORD_COLOR[statusLabel] ?? 'var(--ms-text-muted)';
  const chip = ZONE_CHIP_STYLE[zone];
  const taskRef = task.taskCode ?? String(task.taskId);

  // Petra UAT 2026-08-18 — opening a ticket must not take you off My Tasks.
  // Navigating to /tasks/:ref meant losing the list, and that page's close
  // button then lands on the project board, so a glance at one ticket ended
  // two screens away from where it started. When the host supplies
  // `onRowClick` it owns the presentation (My Tasks opens a slide-over in
  // place); the route stays as the fallback for any host that does not.
  const handleRowClick = useCallback(() => {
    if (logOpen || touchOpen || reviveOpen) return;
    if (onRowClick) {
      onRowClick(task.taskId);
      return;
    }
    navigate(`/tasks/${encodeURIComponent(taskRef)}`);
  }, [navigate, onRowClick, logOpen, touchOpen, reviveOpen, task.taskId, taskRef]);

  const handleRowKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleRowClick();
      }
    },
    [handleRowClick],
  );

  // Phase 86 Plan 86-05 — every zone's chip now opens a real panel:
  // fresh/warm → +LOG, stale → TOUCH (comment composer), graveyard →
  // REVIVE (menu). `MtActionChip.handleClick` already calls
  // `e.stopPropagation()` before invoking this, so opening never bubbles to
  // the row's own click.
  const handleChipClick = useCallback(() => {
    if (zone === 'fresh' || zone === 'warm') {
      setLogOpen((prev) => !prev);
    } else if (zone === 'stale') {
      setTouchOpen((prev) => !prev);
    } else if (zone === 'graveyard') {
      setReviveOpen((prev) => !prev);
    }
  }, [zone]);

  // REVIVE's "Comment" item reuses the SAME composer the TOUCH chip opens
  // (one composer, not two) — close the menu and open the touch popover.
  const handleRequestCommentFromRevive = useCallback(() => {
    setReviveOpen(false);
    setTouchOpen(true);
  }, []);

  // Phase 86-06 — `useMyTasksKeyboard`'s `T` shortcut calls this via the
  // registered handle. It is a no-op on any zone other than `stale` (the
  // only zone whose chip IS the TOUCH action) — "does nothing on a row that
  // has no touch action" (README "Keyboard").
  useEffect(() => {
    if (!registerHandle) return;
    registerHandle(task.taskId, {
      triggerTouch: () => {
        if (zone === 'stale') setTouchOpen(true);
      },
    });
    return () => registerHandle(task.taskId, null);
  }, [registerHandle, task.taskId, zone]);

  // Phase 86-06 — moving the cursor onto this row scrolls it into view when
  // it is off-screen (README "Keyboard").
  useEffect(() => {
    // Optional chaining on the call itself, not just the ref: jsdom (used by
    // this component's own test suite) does not implement `scrollIntoView`
    // at all, unlike every real browser.
    if (isCursor) {
      rowRef.current?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [isCursor]);

  const cursorStyle = isCursor
    ? { backgroundColor: ACCENT_TINT_HEX, boxShadow: CURSOR_RING_BOX_SHADOW }
    : {};

  if (viewportMode === 'card') {
    return (
      <div
        ref={rowRef}
        role="button"
        tabIndex={0}
        aria-current={isCursor ? 'true' : undefined}
        onClick={handleRowClick}
        onKeyDown={handleRowKeyDown}
        data-testid="my-task-row"
        data-viewport-mode="card"
        className={`flex flex-col gap-1.5 p-3 border-b border-ms-border-subtle last:border-0 cursor-pointer ${
          isCursor ? '' : zebraBg
        }`}
        style={cursorStyle}
      >
        {/* Title line — spine dot, code, emoji, title. */}
        <div className="flex items-center gap-2 min-w-0">
          <span
            aria-hidden="true"
            className="w-[3px] h-4 shrink-0 rounded-sm"
            style={{ backgroundColor: rotVisible ? ZONE_SPINE_COLOR[zone] : 'var(--ms-border)' }}
          />
          <span
            className="font-mono text-[10.5px] shrink-0"
            style={{
              color: isUrgentRow ? '#FC8332' : dimmed ? MICRO_LABEL_GREY_HEX : 'var(--ms-text-muted)',
            }}
          >
            {task.taskCode ?? `#${task.taskId}`}
          </span>
          {rotEmoji && (
            <span
              role="img"
              aria-label={rotEmoji.label}
              title={rotEmoji.label}
              className="text-[13px] leading-none shrink-0"
            >
              {rotEmoji.emoji}
            </span>
          )}
          <span
            className={`min-w-0 flex-1 truncate text-[13px] ${
              task.isTerminal
                ? 'text-ms-text-muted line-through font-medium'
                : isUrgentRow
                  ? 'font-semibold text-ms-text-strong'
                  : 'font-medium text-ms-text-strong'
            }`}
            title={task.taskName}
          >
            {task.taskName}
          </span>
        </div>

        {/* Meta line. */}
        <div className="flex items-center gap-2 flex-wrap pl-[11px]">
          <span
            className="font-mono text-[9px] font-bold tracking-[0.1em]"
            style={{ color: statusColor }}
          >
            {statusLabel}
          </span>
          {task.tier && task.slaReactionPct != null && (
            <span
              className="font-mono text-[9px] font-bold tracking-[0.1em]"
              style={{ color: '#9F1239' }}
              data-testid="my-task-row-sla-chip"
            >
              P{task.tier.orderIndex} · SLA R {Math.round(task.slaReactionPct)}%
            </span>
          )}
          {task.unreadCommentCount > 0 && (
            <span
              className={`font-mono text-[9.5px] ${task.hasMention ? 'font-semibold text-ms-orange' : 'text-ms-text-muted'}`}
              data-testid="my-task-row-unread-chip"
            >
              {task.hasMention ? '@ME · ' : ''}
              {task.unreadCommentCount} UNREAD
            </span>
          )}
          {!task.isExternal && (
            <span
              className="font-mono text-[9px] font-bold tracking-[0.1em]"
              style={{ color: MICRO_LABEL_GREY_HEX }}
            >
              INT
            </span>
          )}
          {over && (
            <span
              className="px-1.5 py-0.5 rounded font-mono text-[9px] font-bold tracking-[0.08em]"
              style={{ backgroundColor: OVER_ESTIMATE_CHIP_TINT_HEX, color: '#B45309' }}
              data-testid="my-task-row-over-estimate-chip"
            >
              {task.loggedHours - task.estimatedHours!}H OVER ESTIMATE
            </span>
          )}
        </div>

        {/* Footer — rot meter + due + action. Card mode drops CLIENT and
            LOGGED entirely (README "Responsive"), and the action target is
            grown to the 44px minimum. */}
        <div className="flex items-center justify-between gap-3 pt-1 pl-[11px]">
          {rotVisible ? (
            <MtRotMeter lastActivityAt={task.lastActivityAt} isTerminal={task.isTerminal} />
          ) : (
            <span className="text-[10px] text-ms-text-muted">—</span>
          )}
          <span
            className={`font-mono text-[10.5px] leading-tight ${due.primaryClass}`}
            style={due.primaryColor ? { color: due.primaryColor } : undefined}
          >
            {due.primary}
            {due.secondary && (
              <>
                <br />
                <span className="font-normal" style={{ color: due.secondaryColor }}>
                  {due.secondary}
                </span>
              </>
            )}
          </span>
          <div className="flex items-center justify-center relative shrink-0">
            <MtActionChip
              zone={zone}
              taskCode={taskRef}
              label={chip.label}
              disabled={task.isTerminal}
              onClick={handleChipClick}
              // 44px minimum hit target (README "Responsive"). Only
              // `min-h`/`min-w` are added — a DIFFERENT CSS property than
              // the base component's own `px-2.5 py-1`, so there is no
              // utility-ordering ambiguity about which value wins.
              className="min-h-[44px] min-w-[44px]"
            />
            <LogTimePopover
              taskId={task.taskId}
              taskName={task.taskName}
              projectId={task.projectId}
              open={logOpen}
              onClose={() => setLogOpen(false)}
            />
            <MtTouchPopover
              taskId={task.taskId}
              taskName={task.taskName}
              projectId={task.projectId}
              open={touchOpen}
              onClose={() => setTouchOpen(false)}
            />
            <MtReviveMenu
              taskId={task.taskId}
              taskName={task.taskName}
              projectId={task.projectId}
              projectName={task.projectName}
              open={reviveOpen}
              onClose={() => setReviveOpen(false)}
              onRequestComment={handleRequestCommentFromRevive}
            />
          </div>
        </div>
      </div>
    );
  }

  const resolvedGrid =
    gridTemplateColumns ?? (viewportMode === 'narrow' ? ROW_GRID_NARROW : ROW_GRID);
  const showClientAndLogged = viewportMode !== 'narrow';

  return (
    <div
      ref={rowRef}
      role="button"
      tabIndex={0}
      aria-current={isCursor ? 'true' : undefined}
      onClick={handleRowClick}
      onKeyDown={handleRowKeyDown}
      data-testid="my-task-row"
      data-viewport-mode={viewportMode}
      className={`group relative grid items-center ${padding} ${
        isCursor ? '' : zebraBg
      } border-b border-ms-border-subtle last:border-0 hover:bg-ms-orange/5 focus-within:bg-ms-orange/5 cursor-pointer`}
      style={{ gridTemplateColumns: resolvedGrid, ...cursorStyle }}
    >
      {/* Col 1 — spine, coloured by zone. */}
      <span
        aria-hidden="true"
        className="h-full min-h-[34px] block"
        style={{ backgroundColor: rotVisible ? ZONE_SPINE_COLOR[zone] : 'var(--ms-border)' }}
      />

      {/* Col 2 — task code */}
      <span
        className="font-mono text-[10.5px] pl-3 truncate"
        style={{
          color: isUrgentRow ? '#FC8332' : dimmed ? MICRO_LABEL_GREY_HEX : 'var(--ms-text-muted)',
        }}
      >
        {task.taskCode ?? `#${task.taskId}`}
      </span>

      {/* Col 3 — task title + meta line */}
      <div className="min-w-0 pr-[14px]">
        <div className="flex items-center gap-[7px] min-w-0">
          {rotEmoji && (
            <span
              role="img"
              aria-label={rotEmoji.label}
              title={rotEmoji.label}
              className="text-[13px] leading-none shrink-0"
            >
              {rotEmoji.emoji}
            </span>
          )}
          <span
            className={`min-w-0 flex-1 truncate text-[13px] ${
              task.isTerminal
                ? 'text-ms-text-muted line-through font-medium'
                : isUrgentRow
                  ? 'font-semibold text-ms-text-strong'
                  : 'font-medium text-ms-text-strong'
            }`}
            title={task.taskName}
          >
            {task.taskName}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-[3px] flex-wrap">
          <span
            className="font-mono text-[9px] font-bold tracking-[0.1em]"
            style={{ color: statusColor }}
          >
            {statusLabel}
          </span>
          {task.tier && task.slaReactionPct != null && (
            <span
              className="font-mono text-[9px] font-bold tracking-[0.1em]"
              style={{ color: '#9F1239' }}
              data-testid="my-task-row-sla-chip"
            >
              P{task.tier.orderIndex} · SLA R {Math.round(task.slaReactionPct)}%
            </span>
          )}
          {task.unreadCommentCount > 0 && (
            <span
              className={`font-mono text-[9.5px] ${task.hasMention ? 'font-semibold text-ms-orange' : 'text-ms-text-muted'}`}
              data-testid="my-task-row-unread-chip"
            >
              {task.hasMention ? '@ME · ' : ''}
              {task.unreadCommentCount} UNREAD
            </span>
          )}
          {!task.isExternal && (
            <span
              className="font-mono text-[9px] font-bold tracking-[0.1em]"
              style={{ color: MICRO_LABEL_GREY_HEX }}
            >
              INT
            </span>
          )}
          {over && (
            <span
              className="px-1.5 py-0.5 rounded font-mono text-[9px] font-bold tracking-[0.08em]"
              style={{ backgroundColor: OVER_ESTIMATE_CHIP_TINT_HEX, color: '#B45309' }}
              data-testid="my-task-row-over-estimate-chip"
            >
              {task.loggedHours - task.estimatedHours!}H OVER ESTIMATE
            </span>
          )}
        </div>
      </div>

      {/* Col 4 — client. Dropped entirely (not just hidden) below the
          narrower breakpoint (README "Responsive"). */}
      {showClientAndLogged && (
        <span
          data-testid="my-task-row-client"
          className={`text-[11px] truncate ${dimmed ? 'text-ms-text-muted' : 'text-ms-text-secondary'}`}
        >
          {task.customerName ?? '—'}
        </span>
      )}

      {/* Col 5 — ROT · LAST TOUCH */}
      {rotVisible ? (
        <MtRotMeter lastActivityAt={task.lastActivityAt} isTerminal={task.isTerminal} className="pr-4" />
      ) : (
        <span className="text-[10px] text-ms-text-muted pr-4">—</span>
      )}

      {/* Col 6 — logged / estimate. Dropped entirely (not just hidden) below
          the narrower breakpoint, same as CLIENT. Dimmed (graveyard/terminal)
          rows mute both numerator and denominator; over-estimate always
          wins with bold + danger on the numerator regardless of dim state. */}
      {showClientAndLogged && (
        <span data-testid="my-task-row-logged" className="font-mono text-[11px] tabular-nums">
          <span
            className={`${over ? 'font-bold' : 'font-medium'} ${!over && !dimmed ? 'text-ms-text-strong' : ''}`}
            style={{ color: over ? DANGER_STRONG_HEX : dimmed ? 'var(--ms-text-muted)' : undefined }}
          >
            {task.loggedHours}
          </span>
          <span style={{ color: dimmed ? 'var(--ms-text-muted)' : MICRO_LABEL_GREY_HEX }}>
            /{task.estimatedHours != null ? `${task.estimatedHours}h` : '—'}
          </span>
        </span>
      )}

      {/* Col 7 — due */}
      <span className={`font-mono text-[10.5px] leading-tight ${due.primaryClass}`} style={due.primaryColor ? { color: due.primaryColor } : undefined}>
        {due.primary}
        {due.secondary && (
          <>
            <br />
            <span className="font-normal" style={{ color: due.secondaryColor }}>
              {due.secondary}
            </span>
          </>
        )}
      </span>

      {/* Col 8 — action chip */}
      <div className="flex items-center justify-center relative">
        <MtActionChip
          zone={zone}
          taskCode={taskRef}
          label={chip.label}
          disabled={task.isTerminal}
          onClick={handleChipClick}
        />
        <LogTimePopover
          taskId={task.taskId}
          taskName={task.taskName}
          projectId={task.projectId}
          open={logOpen}
          onClose={() => setLogOpen(false)}
        />
        <MtTouchPopover
          taskId={task.taskId}
          taskName={task.taskName}
          projectId={task.projectId}
          open={touchOpen}
          onClose={() => setTouchOpen(false)}
        />
        <MtReviveMenu
          taskId={task.taskId}
          taskName={task.taskName}
          projectId={task.projectId}
          projectName={task.projectName}
          open={reviveOpen}
          onClose={() => setReviveOpen(false)}
          onRequestComment={handleRequestCommentFromRevive}
        />
      </div>
    </div>
  );
}

export { ROW_GRID as MY_TASK_ROW_GRID, ROW_GRID_NARROW as MY_TASK_ROW_GRID_NARROW };
